import React, { useState, useEffect } from "react"
import tw, { css } from "twin.macro"
import { useStaticQuery, graphql, navigate } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"

import { Button, Container, Footnotes, InlineLink } from "./auvelity-ui"
import ToolTip from "./tooltip"

import navIcon1 from "../images/slides/navIcon1.svg"
import navIcon2 from "../images/slides/navIcon2.svg"
import navIcon3 from "../images/slides/navIcon3.svg"
import navIcon4 from "../images/slides/navIcon4.svg"
import slideIcon1 from "../images/slides/slideIcon-1.svg"
import slideIcon2 from "../images/slides/slideIcon-2.svg"
import slideIcon3 from "../images/slides/slideIcon-3.svg"
import slideIcon4 from "../images/slides/slideIcon-4.svg"
import icon1 from "../images/shapes/grey-oval.svg"
import icon3 from "../images/shapes/yellow-split-square.svg"
import icon5 from "../images/shapes/grey-rectangle-rounded.svg"
import icon6 from "../images/shapes/yellow-circle.png"
import icon7 from "../images/shapes/yellow-triangle.svg"
import icon8 from "../images/shapes/grey-rectangle.png"
import icon9 from "../images/shapes/yellow-rectangle-rounded.svg"
import icon12 from "../images/shapes/grey-rectangle-rounded-bl.svg"
import icon13 from "../images/shapes/geo-square-yellow.svg"
import iconVideo from "../images/icons/ic-video.svg"
import binoculars from "../images/icons/binoculars-white.svg"

const HomeContent = () => {
  const [showVideoModal, setShowVideoModal] = useState(false)
  const Modal = ({ children, isOpen, onClose }) => {
    if (!isOpen) return null;
    return (
      <div tw="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70" onClick={onClose}>
        <div tw="relative rounded-lg  shadow-lg w-full max-w-3xl">
          <button
            onClick={onClose}
            tw="absolute top-[-30px] right-1 text-white text-xl"
            aria-label="Close"
            className="closing-icon"
          >
            &times;
          </button>
          <div>{children}</div>
        </div>
      </div>
    );
  };
  const { slide1, slide2, slide3, slide4, slide4Mobile, videoPlaceholder } =
    useStaticQuery(graphql`
      query {
        slide1: file(relativePath: { eq: "slides/slideBg-1.png" }) {
          childImageSharp {
            gatsbyImageData(width: 497, placeholder: NONE)
          }
        }
        slide2: file(relativePath: { eq: "slides/slideBg-2.png" }) {
          childImageSharp {
            gatsbyImageData(width: 500, placeholder: NONE)
          }
        }
        slide3: file(relativePath: { eq: "slides/slideBg-3.png" }) {
          childImageSharp {
            gatsbyImageData(width: 480, quality: 100, placeholder: NONE)
          }
        }
        slide4: file(relativePath: { eq: "slides/slideBg-4.png" }) {
          childImageSharp {
            gatsbyImageData(width: 591, quality: 100, placeholder: NONE)
          }
        }
        slide4Mobile: file(
          relativePath: { eq: "slides/slideBg-4.png" }
        ) {
          childImageSharp {
            gatsbyImageData(width: 600, placeholder: NONE)
          }
        }
        videoPlaceholder: file(
          relativePath: { eq: "slides/video-placeholder.png" }
        ) {
          childImageSharp {
            gatsbyImageData(width: 600, placeholder: NONE)
          }
        }
      }
    `)

  const data = [
    {
      title: (<>AUVELITY could help you get your <em>GO</em> back</>),
      navTitle: "About",
      image: slide1,
      copy: (
        <>
          AUVELITY is an oral medication for adults with major depressive disorder&nbsp;(MDD).
        </>
      ),
      button: "How AUVELITY may help",
      url: "/how-auvelity-works",
      icon: slideIcon1,
      navIcon: navIcon1,
      bgicons: [icon7, icon5, icon1, icon6],
      actorPortrayal: "white",
      background: "blue",
      first: true
    },
    {
      title: (
        <><em style={{marginRight:'3px'}}>GO</em> with symptom relief that's FAST &amp; LASTS</>
      ),
      navTitle: "Results",
      image: slide2,
      leftMobile: true,
      leftMobileWhite: true,
      copy: (
        <span tw="letter-spacing[-0.4px] xl:(kerning[1] block w-full)">
          Some people saw symptom relief in as early as 1 week in a study vs placebo. On average, significant symptom relief was seen at 6 weeks.<sup>†</sup>
        </span>
      ),
      footnote: (
        <div tw="w-full mb-12 lg:(mb-0 w-3/4)">
          <Footnotes content="&dagger;" superscript>
            <ToolTip
              toolTipData={[
                `An inactive treatment that resembles the medication being studied in a clinical trial.`,
              ]}
              spacer
              wide
            >
              <p>
                Measured against <span>placebo</span> on a depression rating scale during a 6&#8209;week study of 327 adults with MDD.
              </p>
            </ToolTip>
          </Footnotes>
        </div>
      ),
      button: "See the results ",
      navIcon: navIcon2,
      icon: slideIcon2,
      url: "/how-auvelity-may-help",
      bgicons: [icon1, icon13, icon3, icon5],
      actorPortrayal: true,
    },
    {
      title: <>Ready to get started?</>,
      navTitle: "Telehealth",
      image: slide3,
      copy: (
        <>
          Talking to a healthcare provider is the first step in getting the care you need. We can help.
        </>
      ),
      leftMobileCurve: true,
      leftMobile: true,
      button: "Make an appointment ",
      navIcon: navIcon3,
      icon: slideIcon3,
      url: "/connect-with-a-provider",
      addClassForGTM: "fl-make-appointment-cta-home",
      bgicons: [icon1, icon13, icon3, icon12],
      actorPortrayal: true,
      background: "blue",
    },
    {
      title: "Savings can be simple",
      navTitle: "Savings",
      image: slide4,
      alt: "Auvelity On My Side. Pay as little as $10 for up to a 90-day prescription*. *For eligible patients. See Terms & Conditions. Please see Important Safety Information and Medication Guide, including Boxed Warning, provided by the pharmacist.",
      mobileImage: slide4Mobile,
      whitebg: true,
      copy: (
        <>
          <p>Ready to start saving? We've got Savings Cards right here and ready to download for eligible&nbsp;patients.</p>
          <p style={{marginTop: "15px", fontSize: "14px"}}>Must have commercial insurance. Offer not available for patients with Medicare, Medicaid, or other government-funded insurance.</p>
        </>
      ),
      button: "Get the Savings Card ",
      footnote: (
        <span tw="block text-center mb-10 lg:(text-left mb-0)">
          See{" "}
          <InlineLink
            href="/savings-card-terms"
            label="link to terms and conditions. Click here."
            addClassForGTM="gtm-cta-click"
            elementName="terms and conditions"
            target="_blank"
            rel="noreferrer"
            addedStyles={[tw`border-yellow`]}
          >
            link<span css={[tw`sr-only`]}>to terms and conditions </span>
          </InlineLink>{" "}
          for&nbsp;details.
        </span>
      ),
      navIcon: navIcon4,
      leftMobile: true,
      url: "/savings-card",
      icon: slideIcon4,
      bgicons: [icon8, icon9, icon7, icon5],
      actorPortrayal: false,
    },
    {
      title: <>See how AUVELITY could help you get your <strong style={{marginRight:'2px'}}><em>GO</em></strong>&nbsp;back.</>,
      navTitle: "Video",
      copy: (
        <>
          <p>Check out our TV&nbsp;commercial.</p>
        </>
      ),
      navIcon: iconVideo,
      icon: iconVideo,
      addClassForGTM: "fl-make-appointment-cta-home",
      bgicons: [iconVideo],
      actorPortrayal: false,
      background: "blue",
      video: true,
      videoSection: true,
      videoPlaceholder: videoPlaceholder,
      yellow: true
    },
  ]
  // add hashtag scroll
  useEffect(() => {
    const handleScroll = () => {
      const hash = window.location.hash.substring(1);
      if (hash) {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };

    window.onload = handleScroll;

    return () => {
      window.onload = null; // Cleanup to avoid memory leaks
    };
  }, []);

  return (
    <>
    <div tw="bg-white">
      <div tw="mt-[-60px] overflow-hidden rounded-br-[64px] lg:(mt-[-100px] rounded-tl-[64px] rounded-none) relative">
        {data.map((item, index) => {
          const alternate = index % 2 === 1

          return (
            <section
              css={[
                tw`lg:(py-24 rounded-tl-[100px] rounded-br-[100px])`,
                alternate
                  ? tw`bg-lightgrey lg:bg-white text-[#333333]`
                  : tw`bg-blue text-white`,
                item.videoSection
                  ? tw`bg-white text-black` : '',
                item.whitebg && tw`bg-white`,
                item.leftMobileWhite && tw`bg-white`,
                item.leftMobileCurve && tw`rounded-tl-[64px] rounded-br-[64px]`,
                item.yellow && tw`bg-yellow`
              ]}
              key={index}
              className={`${item.video ? 'video-container' : ''}`}
              id={`${item.videoSection ? 'TV-Commercial' : ''}`}
            >
              <Container>
                <div
                  key={index}
                  className={`${item.first ? 'mobile-reverse' : ''}`}
                  css={[
                    tw`flex flex-col w-full items-center text-center pt-16 pb-6 lg:(h-full text-left py-0 flex-row justify-between transition invisible opacity-0 transform translate-y-10 duration-200)`,
                    index === index &&
                      tw`lg:(visible opacity-100 translate-y-0 duration-500)`,
                    item.video ? tw`bg-yellow rounded-tr-[64px] rounded-bl-[64px] lg:(rounded-tl-[64px] rounded-br-[64px] rounded-tr-[0] rounded-bl-[0])` : '',
                  ]}
                >
                  <div
                    className="content-container"
                    css={[
                      tw`w-full min-h-full flex flex-col justify-start items-center relative py-1 lg:(w-2/5 justify-center items-start px-1) `,
                      alternate && tw`lg:order-2`,
                      item.video && tw`lg:order-2 pl-0 pr-0`,
                      item.first && tw`sm:mt-10 lg:mt-0`,
                      item.yellow && tw`w-full justify-center lg:(w-full items-center)`
                    ]}
                    style={item.video ? {marginBottom: '0'} : {}}
                  >
                    <img src={item.icon} css={[tw`width[5rem] mb-2`, item.yellow && tw`width[160px] height[160px] mb-5`, item.video && tw`width[58px] height[58px] lg:(align-self[start])`]} alt=""  />
                    <h2
                      css={[
                        tw`text-2xl text-center text-yellow font-bold leading-tight mb-1 lg:(mt-0 text-4xl mb-4 text-left)`,
                        alternate && tw`text-blue`,
                        item.video && tw`text-blue`,
                        item.leftMobile && tw`text-left w-full`,
                        item.yellow && tw`text-blue text-center`
                      ]}
                    >
                      {item.title}
                    </h2>
                    <p
                      css={[
                        tw`mb-4 px-2 lg:(text-left px-0) xl:(text-lg mb-8)`,
                        item.video && tw`text-black w-full text-center lg:(text-left)`,
                        item.leftMobile && tw`text-left pl-0`
                      ]}
                      style={item.video ? {maxWidth: '100%', marginBottom: '0'} : {}}
                    >
                      {item.copy}
                    </p>
                    {item.button && (
                      <Button
                        arrow
                        variant={ index%2 == 0 ? 'yellow' : 'yellow'}
                        env={ index%2 == 0 ? 'blue' : 'yellow' }
                        onClick={() => navigate(item.url)}
                        addClassForGTM={item.addClassForGTM}
                      >
                        {item.button}
                      </Button>
                    )}
                    <div tw="hidden lg:block">
                      {item.footnote && (
                        <div
                          css={[
                            tw`mt-8 pt-4 text-sm text-left lg:(pt-10 mt-0)`,
                          ]}
                        >
                          {item.footnote}
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    css={[
                      tw`relative px-4 lg:(mr-0 px-0)`,
                      alternate && tw`lg:order-1`,
                      item.video && tw`p-[10px] lg:(order-1 p-[64px]) `
                    ]}
                    style={item.video ? {width: '100%', maxWidth: '500px', margin: '0 auto'} : {}}
                  >
                    {!item.video && (
                      <GatsbyImage
                        image={getImage(item.image)}
                        alt={item.alt}
                        css={[tw`mt-8 lg:mt-0 `, index===0 && tw`mt-8 lg:mt-0 max-w-[497px] `]}
                        objectFit="contain"
                        // {index===1 ? className = 'home-section-image' : className = ''}
                      />
                    )}
                    {item.video && (
                      <>
                        <button
                          onClick={() => setShowVideoModal(true)}
                          tw="mt-8 lg:mt-0 focus:outline-none"
                        >
                          <GatsbyImage
                            image={getImage(item.videoPlaceholder)}
                            alt="Click to watch video"
                            css={[tw`cursor-pointer max-w-[386px]`]}
                            objectFit="contain"
                          />
                        </button>
                      </>
                    )}
                    {item.actorPortrayal && (
                      <div
                        css={[
                          tw`text-xs mt-6 text-white text-center lg:(absolute bottom-[-36px] text-right right-0 text-align[unset] text-ms my-0)`,
                          alternate && tw`text-blue lg:left-0`,
                        ]}
                      >
                        Actor portrayal.
                      </div>
                    )}
                    {item.actorPortrayals && (
                      <div
                        css={[
                          tw`text-xs mt-6 text-white text-center lg:(absolute bottom-[-36px] text-right right-0 text-align[unset] text-ms my-0)`,
                          alternate && tw`text-blue lg:left-0`,
                        ]}
                      >
                        Actor portrayals.
                      </div>
                    )}
                  </div>
                  <div tw="block lg:hidden">
                    {item.footnote && (
                      <div
                        css={[tw`mt-8 pt-4 text-sm text-left lg:(pt-10 mt-0)`]}
                      >
                        {item.footnote}
                      </div>
                    )}
                  </div>
                </div>
              </Container>
            </section>
          )
        })}
      </div>
    </div>
    <Modal isOpen={showVideoModal} onClose={() => setShowVideoModal(false)} style={{margin: "30px"}}>
      <div
        css={[
          tw`w-full max-w-[90vw] md:max-w-[640px] mx-auto`,
          { aspectRatio: "16 / 9", position: "relative", borderRadius: "12px", overflow: "hidden" },
        ]}
      >
        <iframe
          src="https://player.vimeo.com/video/1078036164?h=432b663b72&badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            borderRadius: "12px",
          }}
          title="PP-AUV-US-2400137 Auvelity Patient Ambassador Video- Mecca"
        ></iframe>
      </div>
    </Modal>
    </>
  )
}

export default HomeContent
